
let mapboxgl = null

export default {
  props: {
    hotels: {
      type: Array,
      default: () => []
    },
    selectedHotelId: {
      type: [String, Number],
      default: null
    },
    mobile: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      map: null,
      markers: [],
      hotelMap: {},
      ready: false,
      suppressBounds: false,
      initialFitDone: false,
      popupHovered: false,
      popupLocked: false,
      popupGen: 0,
      clusterSourceReady: false,
      popup: {
        visible: false,
        x: 0,
        y: 0,
        hotel: {},
        stars: 0,
        guestRating: null,
        symbol: '$',
        price: '',
        priceLabel: this.$t('hotels.results.night')
      }
    }
  },
  watch: {
    hotels (newList, oldList) {
      if (!this.ready) return
      const newIds = (newList || []).map(h => h.id).join(',')
      const oldIds = (oldList || []).map(h => h.id).join(',')
      if (newIds !== oldIds) {
        this.syncData()
      }
    },
    '$store.state.hotelPriceDisplayMode' () {
      this.updatePinPrices()
    },
    '$store.state.localCurrency' () {
      this.updatePinPrices()
    },
    selectedHotelId (id) {
      this.markers.forEach(m => {
        const match = id != null && String(m.id) === String(id)
        if (match) {
          m.el.classList.add('selected')
          m.el.style.zIndex = '100'
        } else {
          m.el.classList.remove('selected')
          m.el.style.zIndex = ''
        }
      })
    }
  },
  async mounted () {
    if (!process.client) return
    mapboxgl = (await import('mapbox-gl')).default

    const token = this.$config.MAPBOX_TOKEN
    if (!token) {
      console.warn('MAPBOX_TOKEN not set — map will not render')
      return
    }
    mapboxgl.accessToken = token

    await this.$nextTick()
    if (!this.$refs.mapContainer) return

    this.map = new mapboxgl.Map({
      container: this.$refs.mapContainer,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [0, 20],
      zoom: 2,
      attributionControl: false
    })

    this.map.on('load', () => {
      this.ready = true
      this.map.resize()
      this.syncData()
    })

    this.map.on('move', () => {
      this.repositionPopup()
    })

    this.map.on('moveend', () => {
      this.emitVisibleBounds()
      if (this.clusterSourceReady) {
        this.syncPinMarkers()
      }
      this.repositionPopup()
    })

    this.map.on('click', (e) => {
      if (!e.originalEvent.target.closest('.hotels-map-pin')) {
        if (this.mobile) {
          this.$emit('select', null)
        } else if (this.popupLocked) {
          this.hidePopup()
        }
      }
    })

    setTimeout(() => {
      if (this.map) {
        this.map.resize()
        if (this.mobile && this.hotels && this.hotels.length) {
          this.syncData()
        }
      }
    }, 200)

    if (this.mobile) {
      setTimeout(() => {
        if (this.map) {
          this.map.resize()
          this.syncData()
        }
      }, 600)
    }
  },
  beforeDestroy () {
    this.clearMarkers()
    this.removeClusterLayers()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    // ── Data sync (hotels changed or initial load) ──
    syncData () {
      if (!this.map || !mapboxgl) return

      this.clearMarkers()
      this.hidePopup()

      const validHotels = this.hotels.filter(
        h => h.location && h.location.lat && h.location.lon
      )

      if (!validHotels.length) {
        this.removeClusterLayers()
        return
      }

      // Build lookup map
      this.hotelMap = {}
      validHotels.forEach(h => { this.hotelMap[String(h.id)] = h })

      // Build GeoJSON
      const geojson = {
        type: 'FeatureCollection',
        features: validHotels.map(h => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [h.location.lon, h.location.lat]
          },
          properties: { id: String(h.id) }
        }))
      }

      // Update or create source
      const existingSource = this.map.getSource('hotels-source')
      if (existingSource) {
        existingSource.setData(geojson)
      } else {
        this.map.addSource('hotels-source', {
          type: 'geojson',
          data: geojson,
          cluster: true,
          clusterMaxZoom: 12,
          clusterRadius: 40,
          clusterMinPoints: 30
        })

        // Cluster circles
        this.map.addLayer({
          id: 'hotel-clusters',
          type: 'circle',
          source: 'hotels-source',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#f57418',
            'circle-radius': [
              'step', ['get', 'point_count'],
              18,     // default (2-4)
              5, 22,  // 5+
              10, 27, // 10+
              25, 33  // 25+
            ],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff'
          }
        })

        // Cluster count labels
        this.map.addLayer({
          id: 'hotel-cluster-count',
          type: 'symbol',
          source: 'hotels-source',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 13,
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold']
          },
          paint: {
            'text-color': '#ffffff'
          }
        })

        // Cluster click → zoom to expand (Fix #4)
        this.map.on('click', 'hotel-clusters', (e) => {
          const features = this.map.queryRenderedFeatures(e.point, {
            layers: ['hotel-clusters']
          })
          if (!features.length) return
          const clusterId = features[0].properties.cluster_id
          this.map.getSource('hotels-source').getClusterExpansionZoom(
            clusterId,
            (err, zoom) => {
              if (err) return
              this.map.easeTo({
                center: features[0].geometry.coordinates,
                zoom,
                duration: 500
              })
            }
          )
        })

        // Cursor pointer on cluster hover
        this.map.on('mouseenter', 'hotel-clusters', () => {
          this.map.getCanvas().style.cursor = 'pointer'
        })
        this.map.on('mouseleave', 'hotel-clusters', () => {
          this.map.getCanvas().style.cursor = ''
        })

        // Wait for source to load before syncing markers
        this.map.on('data', (e) => {
          if (e.sourceId === 'hotels-source' && e.isSourceLoaded) {
            this.clusterSourceReady = true
            this.syncPinMarkers()
          }
        })
      }

      this.clusterSourceReady = false
      if (!this.initialFitDone) {
        this.initialFitDone = true
        this.fitAllFromGeoJSON(validHotels)
      }
    },

    // ── Sync HTML markers for unclustered pins ──
    syncPinMarkers () {
      if (!this.map || !this.map.getSource('hotels-source')) return

      const features = this.map.querySourceFeatures('hotels-source', {
        filter: ['!', ['has', 'point_count']]
      })

      // Deduplicate
      const visibleIds = new Set()
      features.forEach(f => {
        if (f.properties && f.properties.id) {
          visibleIds.add(String(f.properties.id))
        }
      })

      // Remove markers whose hotels are now clustered
      const keepMarkers = []
      this.markers.forEach(m => {
        if (visibleIds.has(String(m.id))) {
          keepMarkers.push(m)
        } else {
          m.marker.remove()
        }
      })
      this.markers = keepMarkers

      // Add markers for newly unclustered hotels
      const existingIds = new Set(this.markers.map(m => String(m.id)))
      visibleIds.forEach(id => {
        if (existingIds.has(id)) return
        const hotel = this.hotelMap[id]
        if (!hotel) return
        this.addPinMarker(hotel)
      })
    },

    // ── Create a single HTML pin marker ──
    addPinMarker (hotel) {
      const el = document.createElement('div')
      el.className = 'hotels-map-pin'
      if (this.selectedHotelId != null && String(hotel.id) === String(this.selectedHotelId)) {
        el.classList.add('selected')
      }
      el.dataset.hotelId = hotel.id

      this.setPinText(el, hotel)

      el.addEventListener('click', (e) => {
        e.stopPropagation()
        if (this.mobile) {
          this.$emit('select', hotel.id)
          return
        }
        // Toggle: if this hotel's popup is already open, close it
        if (this.popupLocked && this.popup.visible && String(this.popup.hotel.id) === String(hotel.id)) {
          this.hidePopup()
          return
        }
        this.hidePopup()
        this.showPopup(el, hotel)
        this.popupLocked = true
        this.$emit('select', hotel.id)
      })

      if (!this.mobile) {
        el.addEventListener('mouseenter', () => {
          el.classList.add('hovered')
        })

        el.addEventListener('mouseleave', () => {
          el.classList.remove('hovered')
        })
      }

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([hotel.location.lon, hotel.location.lat])
        .addTo(this.map)

      this.markers.push({ id: hotel.id, marker, el })
    },

    // ── Set price text on a pin element ──
    setPinText (el, hotel) {
      const nights = Math.max(1, this.$store.state.hotelStayNights || 1)
      const useLocal = this.$store.state.localCurrency
      const price = useLocal ? hotel.minPriceLocal : hotel.minPrice
      const symbol = useLocal
        ? (hotel.currencySymbolLocal || '$')
        : (hotel.currencySymbol || '$')
      const displayPrice = this.$store.state.hotelPriceDisplayMode === 'nightly'
        ? Math.ceil(price / nights)
        : Math.ceil(price)
      el.textContent = `${symbol}${displayPrice.toLocaleString()}`
    },

    clearMarkers () {
      this.markers.forEach(m => m.marker.remove())
      this.markers = []
    },

    removeClusterLayers () {
      if (!this.map) return
      if (this.map.getLayer('hotel-cluster-count')) this.map.removeLayer('hotel-cluster-count')
      if (this.map.getLayer('hotel-clusters')) this.map.removeLayer('hotel-clusters')
      if (this.map.getSource('hotels-source')) this.map.removeSource('hotels-source')
      this.clusterSourceReady = false
    },

    // ── Update pin prices without rebuilding ──
    updatePinPrices () {
      if (!this.markers.length) return
      this.markers.forEach(m => {
        const hotel = this.hotelMap[String(m.id)]
        if (!hotel) return
        this.setPinText(m.el, hotel)
      })
    },

    // ── Popup ──
    showPopup (pinEl, hotel) {
      this.popupGen++
      this.popupHovered = false
      const mapRect = this.$el.getBoundingClientRect()
      const pinRect = pinEl.getBoundingClientRect()

      let x = pinRect.left + pinRect.width / 2 - mapRect.left
      let y = pinRect.top - mapRect.top

      const popupHalf = 130
      const popupSpace = 140
      let panX = 0
      let panY = 0

      if (y < popupSpace) {
        panY = -(popupSpace - y + 10)
        y = popupSpace
      }
      if (x < popupHalf + 10) {
        panX = -(popupHalf + 10 - x)
        x = popupHalf + 10
      } else if (x > mapRect.width - popupHalf - 10) {
        panX = mapRect.width - popupHalf - 10 - x
        x = mapRect.width - popupHalf - 10
      }

      if ((panX || panY) && this.map) {
        this.map.panBy([panX, panY], { duration: 300 })
      }

      const nights = Math.max(1, this.$store.state.hotelStayNights || 1)
      const useLocal = this.$store.state.localCurrency
      const rawPrice = useLocal ? hotel.minPriceLocal : hotel.minPrice
      const price = this.$store.state.hotelPriceDisplayMode === 'nightly'
        ? Math.ceil(rawPrice / nights)
        : Math.ceil(rawPrice)
      const symbol = useLocal
        ? (hotel.currencySymbolLocal || '$')
        : (hotel.currencySymbol || '$')

      const s = parseInt(hotel.starRating, 10)
      const g = parseFloat(hotel.guestRating)

      this.popup = {
        visible: true,
        x,
        y,
        hotel,
        stars: (s > 0 && s <= 5) ? s : 0,
        guestRating: (!isNaN(g) && g > 0) ? g.toFixed(1) : null,
        symbol,
        price: price.toLocaleString(),
        priceLabel: this.$store.state.hotelPriceDisplayMode === 'nightly' ? this.$t('hotels.results.night') : this.$t('hotels.results.stay')
      }
    },

    hidePopup () {
      this.popup.visible = false
      this.popupHovered = false
      this.popupLocked = false
    },

    repositionPopup () {
      if (!this.popup.visible || !this.popup.hotel || !this.map) return
      const hid = String(this.popup.hotel.id)
      const found = this.markers.find(m => String(m.id) === hid)
      if (!found) {
        this.hidePopup()
        return
      }
      const mapRect = this.$el.getBoundingClientRect()
      const pinRect = found.el.getBoundingClientRect()
      this.popup.x = pinRect.left + pinRect.width / 2 - mapRect.left
      this.popup.y = pinRect.top - mapRect.top
    },

    scheduleHidePopup () {
      const gen = this.popupGen
      setTimeout(() => {
        if (gen === this.popupGen && !this.popupHovered) {
          this.hidePopup()
        }
      }, 100)
    },

    onPopupLeave () {
      this.popupHovered = false
      if (!this.popupLocked) {
        this.hidePopup()
      }
    },

    onPopupSelect () {
      const hotel = this.popup.hotel
      if (hotel && hotel.rooms && hotel.rooms[0] && hotel.rooms[0].partnerDeeplinkUrl) {
        window.open(hotel.rooms[0].partnerDeeplinkUrl, '_blank')
      }
      this.hidePopup()
    },

    // ── Marker highlighting ──
    highlightMarker (hotelId) {
      for (let i = 0; i < this.markers.length; i++) {
        this.markers[i].el.classList.remove('selected')
        this.markers[i].el.style.zIndex = ''
      }

      if (hotelId != null) {
        const hid = String(hotelId)
        for (let i = 0; i < this.markers.length; i++) {
          if (String(this.markers[i].id) === hid) {
            this.markers[i].el.classList.add('selected')
            this.markers[i].el.style.zIndex = '100'
            break
          }
        }
      }
    },

    // ── Fit bounds ──
    fitAll () {
      if (!this.markers.length || !mapboxgl) return
      const bounds = new mapboxgl.LngLatBounds()
      this.markers.forEach(m => bounds.extend(m.marker.getLngLat()))
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 500 })
    },

    fitAllFromGeoJSON (hotels) {
      if (!hotels.length || !mapboxgl) return
      const bounds = new mapboxgl.LngLatBounds()
      hotels.forEach(h => bounds.extend([h.location.lon, h.location.lat]))
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 })
    },

    emitVisibleBounds () {
      if (!this.map || this.suppressBounds) return
      const bounds = this.map.getBounds()
      this.$emit('boundsChange', {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      })
    },

    panTo (hotelId) {
      if (!this.map || hotelId == null) return
      const hid = String(hotelId)
      const hotel = this.hotelMap[hid]
      if (!hotel) return

      this.hidePopup()
      this.suppressBounds = true
      const lngLat = [hotel.location.lon, hotel.location.lat]
      this.map.once('moveend', () => {
        this.suppressBounds = false
        this.emitVisibleBounds()
      })
      this.map.flyTo({ center: lngLat, zoom: Math.max(this.map.getZoom(), 15), duration: 600 })
      // After fly: highlight marker and open its popup
      setTimeout(() => {
        this.highlightMarker(hotelId)
        const found = this.markers.find(m => String(m.id) === hid)
        if (found) {
          this.showPopup(found.el, hotel)
          this.popupLocked = true
        }
      }, 700)
    },

    panToIfFar (hotelId) {
      if (!this.map || hotelId == null) return
      this.highlightMarker(hotelId)
      if (this.map.getZoom() >= 15) return
      this.panTo(hotelId)
    },

    highlightPin (hotelId) {
      if (hotelId == null) return
      const hid = String(hotelId)
      const found = this.markers.find(m => String(m.id) === hid)
      if (found) {
        found.el.classList.add('hovered')
        found.el.style.zIndex = '50'
      }
    },

    unhighlightPin (hotelId) {
      if (hotelId == null) return
      const hid = String(hotelId)
      const found = this.markers.find(m => String(m.id) === hid)
      if (found) {
        found.el.classList.remove('hovered')
        if (String(this.selectedHotelId) !== hid) {
          found.el.style.zIndex = ''
        }
      }
    },

    zoomIn () {
      if (this.map) this.map.zoomIn()
    },
    zoomOut () {
      if (this.map) this.map.zoomOut()
    }
  }
}
